import t from "../../../../../i18n";
import type { ParsingError } from "../../../../../domain/parser/types";
import type { Output } from "../../../../../api/call";
import { DateTime } from "luxon";
import formatDate from "../../../../../ui/i18n/formatDate";

export type PostQuantities = Output<
  "POST",
  "/projects/:projectId(uuid)/quantities/import"
>["body"]["impactedQuantities"];
export type FormulaVersionDryRunImpact = {
  quantityName: string;
  startsAt: string;
  oldValue: string | null;
  newValue: string | null;
  label: "added" | "removed" | "modified";
};
export type FormulaVersionDryRunImpactGroup = {
  quantityName: string;
  versions: FormulaVersionDryRunImpact[];
};
type FormulaVersionDetail = {
  name: "FORMULA_VERSION";
  startsAt: string;
  oldValue: string | null;
  newValue: string | null;
};
type DryRunImpactDetail = PostQuantities[number]["details"][number] | FormulaVersionDetail;
export type DryRunImportQuantitiesImpact = Output<
  "POST",
  "/projects/:projectId(uuid)/quantities/import"
>["body"]["impactedQuantities"][number]["details"][number]["name"];

export type DryRunImportQuantitiesImpact2 = Output<
  "POST",
  "/projects/:projectId(uuid)/quantities/import"
>["body"];

/** Error Impact  */
export type PartialDryRunImportQuantitiesImpact = Exclude<
  DryRunImportQuantitiesImpact,
  "MISSING_SYSTEM" | "MISSING_EQUIPMENT_TYPE" | "MISSING_EQUIPMENT" | "FORMULA_VERSION"
>;

export type QuantityDryRunError = ParsingError<PartialDryRunImportQuantitiesImpact>;
export const getQuantityDryRunErrorMessage = (error: QuantityDryRunError) => {
  const errorMessage = quantityParsingDryRunInfoMessages[error.kind];
  return errorMessage ? errorMessage(error.data) : "Unknown error";
};

export const getQuantityDryRunInfoMessage = (
  key: PartialDryRunImportQuantitiesImpact,
  quantityNames: string[],
) => {
  const infoMessage = quantityParsingDryRunInfoMessages[key](quantityNames);
  return infoMessage ? infoMessage : "Unknown error";
};

export const getFormulaVersionDryRunInfoMessage = ({
  startsAt,
  oldValue,
  newValue,
  label,
}: FormulaVersionDryRunImpact) => {
  const formattedStartsAt = formatFormulaVersionDate(startsAt);
  if (label === "added")
    return t(
      "quantities.import.dry_run.message.formula_version_line_added",
      formattedStartsAt,
      newValue ?? "",
    );
  if (label === "removed")
    return t(
      "quantities.import.dry_run.message.formula_version_line_removed",
      formattedStartsAt,
      oldValue ?? "",
    );
  return t(
    "quantities.import.dry_run.message.formula_version_line_modified",
    formattedStartsAt,
    oldValue ?? "",
    newValue ?? "",
  );
};

export const getFormulaVersionDryRunHeaderMessage = (quantityName: string) =>
  t("quantities.import.dry_run.message.formula_version_header", quantityName);

const formatFormulaVersionDate = (value: string) => {
  if (!value) return value;
  const parsed = DateTime.fromISO(value, { zone: "utc" });
  if (!parsed.isValid) return value;
  return formatDate(parsed.toJSDate(), "date", "UTC");
};
export const quantityParsingDryRunInfoMessages = {
  /** ------------------- INFO ------------------- */
  ["DELETED_QOI"]: quantityNames =>
    t("quantities.import.dry_run.message.performance_indicator_deleted", quantityNames as string[]),
  ["DELETED_PROCESS_INDICATOR"]: quantityNames =>
    t("quantities.import.dry_run.message.process_indicator_deleted", quantityNames as string[]),
  ["DELETED_ACTIVATION_FILTER"]: quantityNames =>
    t("quantities.import.dry_run.message.analysis_filter_deleted", quantityNames as string[]),
  ["DELETED_METROLOGY_CROSS_FILTER"]: quantityNames =>
    t("quantities.import.dry_run.message.cross_filter_deleted", quantityNames as string[]),
  ["FORMULA_OR_METROLOGY_UPDATED"]: _ => t("quantities.import.dry_run.message.reset_formula"),
  ["FORMULA_OR_METROLOGY_UPDATED_IN_SETUPS"]: _ =>
    t("quantities.import.dry_run.message.reset_formula_simulation_diagnostic"),
  /** ------------------- BLOCKED ------------------- */
  ["BLOCKED_USED_IN_SETUPS"]: errorData => {
    const { setupNames } = errorData.value as { setupNames: string[] };
    return t(
      "quantities.import.dry_run.message.error.blocked_link_setup",
      errorData.key as string,
      setupNames,
    );
  },
  ["BLOCKED_INVALID_SENSOR_TAG"]: errorData =>
    t(
      "quantities.import.dry_run.message.error.blocked_invalid_sensor_tag",
      errorData.key as string,
    ),
  ["BLOCKED_MISSING_SYSTEM"]: errorData =>
    t(
      "setup.quantity.import.error.data_invalid_permission_system_creation",
      errorData.key || "unknown",
    ),
  ["BLOCKED_MISSING_EQUIPMENT_TYPE"]: errorData =>
    t(
      "setup.quantity.import.error.data_invalid_permission_equipment_type_creation",
      errorData.key || "unknown",
    ),
  ["BLOCKED_MISSING_EQUIPMENT"]: errorData =>
    t(
      "setup.quantity.import.error.data_invalid_permission_equipment_creation",
      errorData.key || "unknown",
    ),
  ["BLOCKED_INVALID_FORMULA"]: errorData =>
    t("setup.quantity.import.error.data_invalid_formula", errorData.key || "unknown"),
  ["BLOCKED_NOT_POSSIBLE_TO_USE_TIME_FORMULA_IN_INDIRECT_DEPENDENCY"]: errorData =>
    errorData.value.invalidFormula,
} satisfies {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key in PartialDryRunImportQuantitiesImpact]: (info: any) => string;
};

export const getParseDryRun = (impactOnQuantities: PostQuantities) => {
  const infos: PostQuantities = [];
  const formulaVersionImpacts: FormulaVersionDryRunImpact[] = [];
  const categoryCreationImpacts: {
    systems: string[];
    equipmentTypes: string[];
    equipments: string[];
  } = { systems: [], equipmentTypes: [], equipments: [] };
  const errors: QuantityDryRunError[] = [];

  for (const quantity of impactOnQuantities) {
    const { target: quantityName, action, details } = quantity;
    for (const detail of details as DryRunImpactDetail[]) {
      switch (detail.name) {
        case "BLOCKED_USED_IN_SETUPS": {
          errors.push({
            kind: detail.name as unknown as PartialDryRunImportQuantitiesImpact,
            explain: "Deleting a setup linked to a setup",
            location: { file: "", line: 0 },
            data: {
              key: quantityName,
              hint: action,
              value: { setupNames: detail.setupNames },
            },
          });
          break;
        }
        case "BLOCKED_NOT_POSSIBLE_TO_USE_TIME_FORMULA_IN_INDIRECT_DEPENDENCY":
        case "BLOCKED_INVALID_FORMULA": {
          const { value: invalidFormula } = detail;
          errors.push({
            kind: detail.name as unknown as PartialDryRunImportQuantitiesImpact,
            explain: "invalid formula",
            location: { file: "", line: 0 },
            data: {
              key: quantityName,
              hint: action,
              value: {
                invalidFormula,
              },
            },
          });
          break;
        }
        case "BLOCKED_INVALID_SENSOR_TAG": {
          const { value: invalidSensorTag } = detail;
          errors.push({
            kind: detail.name as unknown as PartialDryRunImportQuantitiesImpact,
            explain: "invalid sensorTag",
            location: { file: "", line: 0 },
            data: {
              key: quantityName,
              hint: action,
              value: {
                invalidSensorTag,
              },
            },
          });
          break;
        }
        case "BLOCKED_MISSING_SYSTEM":
        case "BLOCKED_MISSING_EQUIPMENT":
        case "BLOCKED_MISSING_EQUIPMENT_TYPE": {
          const { value: categoryName } = detail;
          errors.push({
            kind: detail.name as unknown as PartialDryRunImportQuantitiesImpact,
            explain: "permission for category creation",
            location: { file: "", line: 0 },
            data: {
              key: quantityName,
              hint: action,
              value: {
                categoryName,
              },
            },
          });
          break;
        }
        case "MISSING_SYSTEM": {
          const { value: systemName } = detail;
          categoryCreationImpacts.systems.push(systemName);
          break;
        }
        case "MISSING_EQUIPMENT_TYPE": {
          const { value: equipmentType } = detail;
          categoryCreationImpacts.equipmentTypes.push(equipmentType);
          break;
        }
        case "MISSING_EQUIPMENT": {
          const { value: equipment } = detail;
          categoryCreationImpacts.equipments.push(equipment);
          break;
        }
        case "DELETED_QOI":
        case "DELETED_PROCESS_INDICATOR":
        case "DELETED_ACTIVATION_FILTER":
        case "DELETED_METROLOGY_CROSS_FILTER":
        case "FORMULA_OR_METROLOGY_UPDATED":
        case "FORMULA_OR_METROLOGY_UPDATED_IN_SETUPS":
          infos.push(quantity);
          break;
        case "FORMULA_VERSION": {
          const formulaDetail = detail as FormulaVersionDetail;
          const label =
            formulaDetail.oldValue === null && formulaDetail.newValue !== null
              ? "added"
              : formulaDetail.oldValue !== null && formulaDetail.newValue === null
                ? "removed"
                : "modified";
          formulaVersionImpacts.push({
            quantityName,
            startsAt: formulaDetail.startsAt,
            oldValue: formulaDetail.oldValue,
            newValue: formulaDetail.newValue,
            label,
          });
          break;
        }
        default:
          throw new Error("Unhandled impact detail");
      }
    }
  }

  const quantityNameByImpactInfo = infos.reduce(
    (acc, info) => {
      const { target: quantityName, details } = info;
      for (const detailImpact of details) {
        if (detailImpact.name in acc) {
          acc[detailImpact.name] = Array.from(
            new Set([...(acc[detailImpact.name] ?? []), quantityName]),
          ).sort((a, b) => a.localeCompare(b));
        } else {
          acc[detailImpact.name] = [quantityName];
        }
      }
      return acc;
    },
    {} as Record<string, string[]>,
  );

  const formulaVersionImpactsByQuantity = Array.from(
    formulaVersionImpacts
      .reduce((acc, impact) => {
        const values = acc.get(impact.quantityName) ?? [];
        values.push(impact);
        acc.set(impact.quantityName, values);
        return acc;
      }, new Map<string, FormulaVersionDryRunImpact[]>())
      .entries(),
  )
    .map(([quantityName, versions]) => ({
      quantityName,
      versions: versions.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    }))
    .sort((a, b) => a.quantityName.localeCompare(b.quantityName));

  return {
    errors,
    quantityNameByImpactInfo,
    categoryCreationImpacts,
    formulaVersionImpacts: formulaVersionImpactsByQuantity,
  };
};
